import { getApps } from "firebase/app";
import {
  getFirestore,
  collection,
  collectionGroup,
  WithFieldValue,
  FirestoreDataConverter,
} from "firebase/firestore";

//
// export * from "./privateUserConverter";
// export * from "./formulaConverter";
// export * from "./ruleConverter";
// export * from "./vehicleConverter";
// export * from "./bankConverter";
// export * from "./applicationConverter";
// export * from "./expenseConverter";
// export * from "./leadConverter";
// export * from "./companyConverter";
// export * from "./inventoryListConverter";
import {
  dataPoint,
  dataPointWithConverter,
  collectionGroupDataPointWithConverter,
  collectionGroupDataPoint,
} from "./generics";
import {
  privateUserConverter,
  formulaConverter,
  vehicleConverter,
  bankConverter,
  applicationConverter,
  expenseConverter,
  expectedExpenseConverter,
  leadConverter,
  inventoryLeadConverter,
  customerNoteConverter,
  prospectConverter,
  prospectNoteConverter,
  contactConverter,
  contactCompanyConverter,
  terminalConverter,
  marketingCampaignConverter,
  marketingAudienceConverter,
  campaignRecipientConverter,
  quickLeadConverter,
  companyConverter,
  inventoryListConverter,
  userInventoryListConverter,
  formulaRuleConverter,
  ltvDraftConverter,
  taskConverter,
  featureFlagConverter,
} from "../converters";
import {
  Application,
  Bank,
  BusinessSubmission,
  BusinessInquiry,
  Company,
  Expense,
  Formula,
  FormulaRule,
  Lead,
  Vehicle,
  InventoryList,
  PrivateUserInfoType,
  INotification,
  LtvDraft,
} from "../index";

const createDb = () => ({
  // User collections
  get users() {
    return dataPointWithConverter("users", privateUserConverter);
  },

  // Application and Banking
  get applications() {
    return dataPointWithConverter("applications", applicationConverter);
  },
  get banks() {
    return dataPointWithConverter("banks", bankConverter);
  },

  // Business submissions
  get businessSubmissions() {
    return dataPoint<BusinessSubmission>("businessSubmissions");
  },
  get businessInquiries() {
    return dataPoint<BusinessInquiry>("businessInquiries");
  },

  // Company management
  get companies() {
    return dataPointWithConverter("companies", companyConverter);
  },

  // Financial tracking
  get expenses() {
    return dataPointWithConverter("expenses", expenseConverter);
  },
  get expected_expenses() {
    return dataPointWithConverter("expected_expenses", expectedExpenseConverter);
  },

  // Inventory and vehicle management
  get master_inventory() {
    return dataPointWithConverter("master_inventory", vehicleConverter);
  },
  get shield_inventory() {
    return dataPointWithConverter("shield_inventory", vehicleConverter);
  },
  get inventoryLists() {
    return dataPointWithConverter("inventoryLists", inventoryListConverter);
  },
  get userInventoryLists() {
    return dataPointWithConverter("userInventoryLists", userInventoryListConverter);
  },

  // Sales and leads
  get leads() {
    return dataPointWithConverter("leads", leadConverter);
  },
  get inventory_leads() {
    return dataPointWithConverter("inventory_leads", inventoryLeadConverter);
  },
  get customer_notes() {
    return dataPointWithConverter("customer_notes", customerNoteConverter);
  },
  get prospects() {
    return dataPointWithConverter("prospects", prospectConverter);
  },
  get prospect_notes() {
    return dataPointWithConverter("prospect_notes", prospectNoteConverter);
  },
  get contacts() {
    return dataPointWithConverter("contacts", contactConverter);
  },
  get contact_companies() {
    return dataPointWithConverter("contact_companies", contactCompanyConverter);
  },
  get terminals() {
    return dataPointWithConverter("terminals", terminalConverter);
  },
  get marketing_campaigns() {
    return dataPointWithConverter("marketing_campaigns", marketingCampaignConverter);
  },
  get marketing_audiences() {
    return dataPointWithConverter("marketing_audiences", marketingAudienceConverter);
  },
  get campaign_recipients() {
    return dataPointWithConverter("campaign_recipients", campaignRecipientConverter);
  },
  get quick_leads() {
    return dataPointWithConverter("quick_leads", quickLeadConverter);
  },

  // CRM tasks
  get tasks() {
    return dataPointWithConverter("tasks", taskConverter);
  },

  // Pricing and formulas
  get formulas() {
    return dataPointWithConverter("gray_book_formulas", formulaConverter);
  },

  // LTV drafts / saved reports
  get ltvDrafts() {
    return dataPointWithConverter("ltvDrafts", ltvDraftConverter);
  },

  // Notifications
  get notifications() {
    return dataPoint<INotification>("notifications");
  },

  get featureFlags() {
    return dataPointWithConverter("featureFlags", featureFlagConverter);
  },

  // Sub-collections
  getFormulaRules: (formulaId: string) =>
    dataPointWithConverter(`gray_book_formulas/${formulaId}/rules`, formulaRuleConverter),

  userApplications: (userId: string) =>
    dataPointWithConverter(
      `users/${userId}/applications`,
      applicationConverter
    ),

  userNotifications: (userId: string) =>
    dataPoint<INotification>(`users/${userId}/notifications`),

  companyUsers: (companyId: string) =>
    dataPointWithConverter(
      `companies/${companyId}/users`,
      privateUserConverter
    ),

  // Collection groups for cross-collection queries
  cg: {
    get expenses() {
      return collectionGroupDataPointWithConverter(
        "expenses",
        expenseConverter
      );
    },
    get notifications() {
      return collectionGroupDataPoint<INotification>("notifications");
    },
    get formulaRules() {
      return collectionGroupDataPointWithConverter<FormulaRule>(
        "rules",
        formulaRuleConverter
      );
    },
  },
});

let _db: ReturnType<typeof createDb> | null = null;

/**
 * Get the database instance (lazy initialization)
 */
const getDb = () => {
  if (!_db) {
    _db = createDb();
  }
  return _db;
};

/**
 * Database with lazy initialization using Proxy - prevents Firebase initialization during import
 */
export const db = new Proxy({} as ReturnType<typeof createDb>, {
  get(target, prop) {
    const database = getDb();
    return database[prop as keyof typeof database];
  },
  has(target, prop) {
    const database = getDb();
    return prop in database;
  },
});

export default db;
